import * as Sentry from '@sentry/react-native';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { YStack } from 'tamagui';

import { Text } from '@/components/text';
import { PrimaryButton } from '@/components/primary-button';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

// Issue #126 AC1 / #204: no ErrorBoundary existed anywhere in this app
// (confirmed via grep -- zero matches). An uncaught render exception
// anywhere below this point previously unmounted the ENTIRE React
// tree with no fallback, leaving a truly blank screen (no BackLink, no
// spinner, nothing) rather than a recoverable error state -- the
// leading hypothesis for issue #204's live "/users/{id} renders fully
// blank" report. A class component is required here: React's error
// boundary API (getDerivedStateFromError/componentDidCatch) has no
// hooks equivalent.
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // console.error stays alongside the Sentry report (issue #142) --
    // still useful in local dev, where EXPO_PUBLIC_SENTRY_DSN is
    // typically unset and Sentry.init leaves the SDK disabled.
    console.error('Uncaught render error', error, errorInfo);
    Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
  }

  handleRetry = (): void => {
    this.setState({ hasError: false });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <YStack flex={1} alignItems="center" justifyContent="center" gap="$3" backgroundColor="$background" px="$6">
          <Text color="$color" textAlign="center" fontWeight="$semibold">
            Something went wrong.
          </Text>
          <Text color="$colorSecondary" textAlign="center">
            Check your connection and try again.
          </Text>
          <PrimaryButton onPress={this.handleRetry}>Try again</PrimaryButton>
        </YStack>
      );
    }

    return this.props.children;
  }
}
